import { User } from './user';
import { UserRole, UserRoleModel } from './user-role';
import { Role } from './role';
import { Person } from './person';
import { RoleEvaluator } from './role-evaluator';
import { Organization } from './organization';
import { ActiveStatus, ActiveStatuses } from './active-status';
import { SoftwareSystem } from './software-system';
import { ProgramType } from './program-type';

export type AccessLevel = 'PUBLIC' | 'AUTH' | 'PROTECTED' | 'PRIVATE';

export const AccessLevels = {
  PUBLIC: 'PUBLIC' as AccessLevel,
  AUTH: 'AUTH' as AccessLevel,    // The AUTH level indicates the fields needed for authentication
  PROTECTED: 'PROTECTED' as AccessLevel,
  PRIVATE: 'PRIVATE' as AccessLevel
};

export interface CreateOptions {
  // Ignore potentially sensitive / unused properties
  accessLevel: AccessLevel;
}

const DEFAULT_STATUSES = ActiveStatuses.ALL
  .filter(status => status !== ActiveStatuses.INACTIVE);

export const DEFAULT_CREATE_OPTIONS = {
  accessLevel: AccessLevels.PROTECTED
} as CreateOptions;

export class UserModel implements User {
  id: number;
  code?: string;
  programType?: ProgramType;
  clazz: string;
  // Group name, App name or person's preferred first name and last name
  name: string;
  displayName?: string;
  roles: UserRole[];

  // Person fields
  firstName?: string;
  firstNamePreferred?: string;
  middleName?: string;
  lastName?: string;
  fullName?: string;
  email?: string;
  phone?: string;
  loginId?: string;
  employeeId?: string;
  studentId?: string;
  pidm?: number;
  homeDepartmentName?: string;
  primaryTitle?: string;
  impersonator?: Person;

  // Software fields
  url?: string;

  static create(
    props: Partial<User | Person | Organization | SoftwareSystem>,
    options: CreateOptions = DEFAULT_CREATE_OPTIONS
  ): User | null {
    if (!props || (!props.id && !props.code) || !props.name) {
      return null;
    }

    return new UserModel(props, options);
  }

  static validate(props: Partial<User | Person | Organization>): string[] {
    const errors: string[] = [];

    if (props.id && !/^\d{1,10}$/g.test(props.id.toString())) {
      errors.push('Invalid ID');
    }
    if (props.code && !/^[A-Z]{2,50}$/g.test(props.code)) {
      errors.push('Invalid code');
    }
    if (!props.name || typeof props.name !== 'string' || props.name.length > 200) {
      errors.push('Invalid name');
    }
    if (props.displayName && ( typeof props.displayName !== 'string' || props.displayName.length > 200) ) {
      errors.push('Invalid display name');
    }
    if (!props.email || typeof props.email !== 'string' || props.email.length > 200 || !props.email.includes('@')) {
      errors.push('Invalid email');
    }

    const personProps = props as Partial<Person>;
    if (personProps.loginId && !/^[a-z][a-z0-9]{1,11}$/g.test(personProps.loginId)) {
      errors.push('Invalid loginId');
    }
    if (personProps.employeeId && !/^[0-9]{8,9}$/g.test(personProps.employeeId)) {
      errors.push('Invalid employee ID');
    }
    if (personProps.studentId && !/^[89][0-9]{8}$/g.test(personProps.studentId)) {
      errors.push('Invalid student ID');
    }

    return errors;
  }

  static sort(user1?: User, user2?: User) {
    const name1 = user1 && ( user1.name || user1.displayName ) || '';
    const name2 = user2 && ( user2.name || user2.displayName ) || '';

    const compare = name1.localeCompare(name2);

    if (compare !== 0) {
      return compare;
    }

    if (!user1 || !user1.id) {
      return -1;
    }

    if (!user2 || !user2.id) {
      return 1;
    }

    return user1.id.toString().localeCompare(user2.id.toString());
  }

  constructor(
    props: Partial<User | Person | Organization | SoftwareSystem>,
    options: CreateOptions = DEFAULT_CREATE_OPTIONS
  ) {
    this.id = props.id || 0;
    this.name = props.name || '';
    this.displayName = props.displayName;
    this.email = props.email;

    // Only include "roles" in the PROTECTED or PRIVATE access levels
    if (options.accessLevel === AccessLevels.PROTECTED || options.accessLevel === AccessLevels.PRIVATE) {
      if (props.roles) {
        this.roles = props.roles
          .map(userRole => UserRoleModel.create(userRole));
      }
    }

    if ('url' in props) {
      this.clazz = 'edu.ucdavis.gs.people.SoftwareSystem';
      this.url = (props as Partial<SoftwareSystem>).url;
    }

    if ('firstName' in props || 'lastName' in props || ( props.clazz && props.clazz.indexOf('Person') > -1 )) {
      const personProps = (props as Partial<Person>);

      // In the case where all we have is the person's full name, break up the name into first and last
      const nameParts = !personProps.lastName && props.name ? props.name.split(',') : [];

      if (personProps.lastName) {
        this.name = personProps.fullName || personProps.lastName + ', ' + personProps.firstName;
      }

      this.clazz = 'edu.ucdavis.gs.people.Person';
      this.firstName = personProps.firstName || ( nameParts.length > 1 ? parseFirstName(nameParts[1]) : undefined );
      this.firstNamePreferred = personProps.firstNamePreferred;
      this.middleName = personProps.middleName || ( nameParts.length > 1 ? parseMiddleName(nameParts[1]) : undefined );
      this.lastName = personProps.lastName || ( nameParts.length ? nameParts[0] : undefined );
      this.fullName = personProps.fullName || this.name;
      this.loginId = personProps.loginId;
      this.phone = personProps.phone;

      if (!this.displayName) {
        this.displayName = (this.firstNamePreferred || this.firstName) + ' ' + this.lastName;
      }

      if (options.accessLevel !== AccessLevels.PUBLIC) {
        this.employeeId = personProps.employeeId;
        this.studentId = personProps.studentId;
        this.pidm = personProps.pidm;
      }

      // Only include "homeDepartmentName" and "primaryTitle" in the PROTECTED or PRIVATE access levels
      if (options.accessLevel === AccessLevels.PROTECTED || options.accessLevel === AccessLevels.PRIVATE) {
        this.homeDepartmentName = personProps.homeDepartmentName;
        this.primaryTitle = personProps.primaryTitle;
      }
    } else {
      this.code = props.code;

      if (props.clazz) {
        this.clazz = props.clazz as string;
      }

      if (!this.clazz) {
        if (props.code === 'HACS' || /^[A-Z]{2,3}$/g.test(props.code as string)) {
          this.clazz = 'edu.ucdavis.gs.people.CollegeUnit';
        } else if (/^[A-Z]{4}$/g.test(props.code as string)) {
          this.clazz = 'edu.ucdavis.gs.people.MajorProgram';
        } else {
          this.clazz = 'edu.ucdavis.gs.people.Department';
        }
      }

      if (this.clazz.endsWith('MajorProgram')) {
        this.programType = (props as Partial<Organization>).programType;
      }
    }

    this.impersonator = props.impersonator && UserModel.create(props.impersonator) as Person;
  }

  isOfType(this: User, className: string): boolean {
    if (this.clazz) {
      return this.clazz.indexOf(className) > -1;
    }

    if (className === 'Person') {
      return ( 'firstName' in this || 'lastName' in this );
    }

    if (className === 'SoftwareSystem') {
      return ( 'url' in this );
    }

    return !!this.code;
  }

  findRoles(roleToFind: string | Role, statuses: ActiveStatus[] = DEFAULT_STATUSES): UserRole[] {
    if (!roleToFind || !this.roles) {
      return [];
    }

    const roleCode = typeof roleToFind === 'string' ? roleToFind : roleToFind.code;

    return this.roles
      .filter(userRole =>
        roleCode === userRole.role.code &&
        ( statuses.length === 0 || statuses.includes(userRole.activeStatusCode) )
      );
  }

  isA(roleToTest: string | Role, statuses: ActiveStatus[] = DEFAULT_STATUSES): RoleEvaluator {
    return new RoleEvaluator(this, roleToTest, statuses);
  }

  isUnder(userOrIdOrCode?: User | Person | Organization | number | string): boolean {
    if (!userOrIdOrCode) {
      return false;
    }

    if (
      this === userOrIdOrCode ||
      this.id === userOrIdOrCode ||
      this.code === userOrIdOrCode ||
      this.code === (userOrIdOrCode as User).code ||
      this.id === (userOrIdOrCode as User).id
    ) {
      return true;
    }

    return this.roles != null &&
      this.roles
        .map(userRole => userRole.role)
        // If the current user is a person or software look at all roles, otherwise only look at hierarchical roles
        .filter(role =>
          this.isOfType('Person') || this.isOfType('SoftwareSystem') || role.isHierarchical
        )
        .some(role => this.isA(role).of(userOrIdOrCode));
  }

  isUnderAny(userOrIdOrCodes: (User | Person | Organization | number | string)[]): boolean {
    return userOrIdOrCodes.some(item => this.isUnder(item));
  }

  toString() {
    return `User(id: ${this.id}${this.email ? `, email: ${this.email}` : `, name: ${this.name}`})`;
  }
}

function parseFirstName(firstAndMiddle: string) {
  return firstAndMiddle.trim().split(' ')[0];
}

function parseMiddleName(firstAndMiddle: string) {
  const cleanName = firstAndMiddle.trim();
  const spacePos = cleanName.indexOf(' ');

  return spacePos > -1 ? cleanName.substr(spacePos + 1) : undefined;
}



// WEBPACK FOOTER //
// ./src/gs-core/common/models/user.model.ts